import {
  ChangeDetectionStrategy,
  Component,
  computed,
  effect,
  inject,
  input,
  output,
  ResourceRef,
  signal,
} from '@angular/core';
import { rxResource } from '@angular/core/rxjs-interop';
import { MatButtonModule } from '@angular/material/button';
import { MatCardModule } from '@angular/material/card';
import { MatIconModule } from '@angular/material/icon';
import { MatPaginatorModule, PageEvent } from '@angular/material/paginator';
import { MatTableModule } from '@angular/material/table';
import { MatTooltipModule } from '@angular/material/tooltip';
import type { WaterUserMediaBatch, WaterUserMediaBatchListResponse } from '@models/water-user-media-batches';
import { TranslatePipe } from '@ngx-translate/core';
import { showGenericErrorToast } from '@services/show-generic-error-toast';
import { ToastService } from '@services/toast.service';
import { WaterUserMediaService } from '@services/water-user-media.service';
import { WaterUserMediaGridComponent } from './water-user-media-grid.component';

@Component({
  selector: 'app-water-user-media-batch-list',
  templateUrl: './water-user-media-batch-list.component.html',
  styleUrl: './water-user-media-batch-list.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    MatCardModule,
    MatTableModule,
    MatPaginatorModule,
    MatIconModule,
    MatButtonModule,
    MatTooltipModule,
    TranslatePipe,
    WaterUserMediaGridComponent,
  ],
})
export class WaterUserMediaBatchListComponent {
  private readonly mediaService = inject(WaterUserMediaService);
  private readonly toastService = inject(ToastService);

  readonly waterId = input.required<number>();
  /** Emits the total batch count so the parent form can show it as a tab badge. */
  readonly totalCount = output<number>();

  readonly page = signal(1);
  readonly pageSize = signal(25);

  /** When set, the tab shows that batch's media grid instead of the batch table. */
  readonly selectedBatch = signal<WaterUserMediaBatch | null>(null);

  readonly displayedColumns = ['submitter', 'email', 'site', 'items'];

  readonly batchesResource: ResourceRef<WaterUserMediaBatchListResponse | undefined> = rxResource({
    params: () => ({ waterId: this.waterId(), page: this.page(), pageSize: this.pageSize() }),
    stream: ({ params }) =>
      this.mediaService
        .getBatches(params.waterId, params.page, params.pageSize)
        .pipe(showGenericErrorToast(this.toastService, () => undefined)),
  });

  readonly batches = computed(() => this.batchesResource.value()?.items ?? []);

  readonly total = computed(() => this.batchesResource.value()?.totalCount ?? 0);

  constructor() {
    effect(() => this.totalCount.emit(this.total()));
  }

  onPageChange(event: PageEvent): void {
    this.page.set(event.pageIndex + 1);
    this.pageSize.set(event.pageSize);
  }

  openBatch(batch: WaterUserMediaBatch): void {
    this.selectedBatch.set(batch);
  }

  backToList(): void {
    this.selectedBatch.set(null);
  }
}
